import { bound } from "@frui.ts/helpers/dist";
import { watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, ManualEntityValidator, validateAll } from "@frui.ts/validation/dist";
import * as Sentry from "@sentry/react";
import { Modules } from "config";
import { action, computed, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import { BankAccountFormatType } from "entities/bankAccountFormatType";
import OptionsService from "services/optionsService";
import AlertsViewModel from "../../alertsViewModel";
import CreateViewModel from "../../createViewModel";
import LustrationReportCreateRequest, { LustrationReportCreateValidationRules } from "entities/lustrationReportCreateRequest";
import LustrationRepository from "repositories/lustrationRepository";
import CountrySelectViewModel from "viewModels/countrySelectViewModel";
import ReportStatus from "entities/reportStatus";
import { lustrationReportFinish, lustrationReportProcessed } from "services/eventBus";
import type { interfaces } from "inversify";
import LustrationReportResponse from "entities/lustrationReportResponse";
import { subDays } from "date-fns";

export default class LustrationReportsCreateViewModel extends CreateViewModel<LustrationReportCreateRequest> {
  readonly modelName = "lustrationReport";
  private unsubscriptions: Array<() => void> = [];
  countrySelectViewModel: CountrySelectViewModel;
  private manualValidator = new ManualEntityValidator<LustrationReportCreateRequest>(true);

  @computed
  get accountFormatTypes() {
    return this.optionsService.internalEnumOptions(BankAccountFormatType);
  }

  constructor(
    protected repository: LustrationRepository,
    protected localizationService: LocalizationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    private optionsService: OptionsService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    public alertsViewModel: AlertsViewModel
  ) {
    super();
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.unsubscriptions.push(
      reaction(() => [this.item._eventOccurredAtTime, this.item._eventOccurredAtDate], this.onOccurredAtChange)
    );
  }

  @bound
  @watchBusy
  async create() {
    if (validateAll([this.item]) && this.onOccurredAtChange()) {
      try {
        const result = await this.repository.create(this.item);
        if (result.status === ReportStatus.ToBeProcessed) {
          this.eventBus.publish(lustrationReportProcessed({ lustrationReport: result }));
        }
        if (result.status === ReportStatus.Processed) {
          this.eventBus.publish(lustrationReportFinish({ lustrationReport: result }));
        }
        return this.onHide();
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  @action.bound
  private onOccurredAtChange() {
    if (!this.setEventOccurredAt()) {
      return;
    }
    if (this.item._eventOccurredAtTime && this.item.eventOccurredAt && this.item.eventOccurredAt < subDays(new Date(), 731)) {
      this.manualValidator.addError("_eventOccurredAtTime", this.translate("afterOccurredDate"));
      return false;
    } else {
      this.manualValidator.removeError("_eventOccurredAtTime");
      return true;
    }
  }

  @action.bound
  private setEventOccurredAt() {
    if (!this.item._eventOccurredAtTime) {
      return false;
    }
    const [hours, minutes, seconds] = this.item._eventOccurredAtTime.split(":");
    const timeDate = new Date(this.item._eventOccurredAtDate);
    timeDate.setHours(parseInt(hours, 10));
    timeDate.setMinutes(parseInt(minutes, 10));
    timeDate.setSeconds(parseInt(seconds, 10));
    this.item.eventOccurredAt = timeDate;
    return true;
  }

  @action.bound
  @watchBusy
  public async reGenerate(report?: LustrationReportResponse) {
    const reqObj = new LustrationReportCreateRequest();
    if (report?.vehicleRegistration?.countryId) {
      reqObj.countryId = report?.vehicleRegistration?.countryId;
    }
    if (report?.vehicleRegistration?.licensePlate) {
      reqObj.licensePlate = report?.vehicleRegistration?.licensePlate;
    }
    if (report?.eventOccurredAt) {
      reqObj.eventOccurredAt = report?.eventOccurredAt;
    }
    reqObj.forceRequest = true;
    if (validateAll([reqObj])) {
      try {
        await this.repository.create(reqObj);
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  protected loadDetail() {
    const item = new LustrationReportCreateRequest();
    attachAutomaticValidator(item, {
      ...LustrationReportCreateValidationRules,
      _eventOccurredAtTime: {
        ...LustrationReportCreateValidationRules._eventOccurredAtTime,
        manualValidation: this.manualValidator,
      },
    });
    return item;
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }
  @action.bound
  onHide() {
    this.requestClose();
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.enforcement, `lustrationReports.${code}`, size);
  }

  translateAttribute(code: string, size?: number): string {
    return this.localizationService.translateAttribute("lustrationReports", code, size);
  }

  static Factory({ container }: interfaces.Context) {
    return () =>
      new LustrationReportsCreateViewModel(
        container.get(LustrationRepository),
        container.get(LocalizationService),
        container.get(SecurityService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(OptionsService),
        container.get(CountrySelectViewModel.Factory),
        container.get(AlertsViewModel)
      );
  }
}
